import { Card } from "~/Configurators/Linear/components/Card";
import classes from "./DataSheetDownload.module.less";
import { Link, LinkButton } from "~/common/components/Link";
import { useOnMount } from "~/common/hooks/useOnMount";
import { generatePdfDownloadUrl } from "~/ProductDetails/components/SpecificationsCard/components/PdfDownloadLink";
import { trackIntermediatePageLanding } from "~/feature/Analytics/ga/ga";
import type { ProductData } from "~/models/ProductData";
import { downloadBlobFromResponse, getCsrfValue } from "~/api/ApiHelper";
import type { UnitMode } from "~/feature/UnitMode/UnitMode";
import { useUnitMode } from "~/feature/UnitMode/useUnitMode";
import { Trans, useTranslation } from "react-i18next";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { ProductContext } from "~/feature/Product/ProductContext";
import { Product } from "~/feature/Product";
import { styled, Typography } from "@mui/material";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { resolveUrl } from "~/utils/url/urlResolver";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";

interface DataSheetDownloadProps {
  product: ProductData;
}

interface ProductCardProps {
  product: ProductData;
}

const downloadPdf = (product: ProductData, unitMode: UnitMode): Promise<unknown> => {
  const url = generatePdfDownloadUrl(product, unitMode);
  return fetch(resolveUrl(url), {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "GET",
    headers: {
      "content-type": "application/pdf",
      CSRFToken: getCsrfValue(),
    },
  }).then(downloadBlobFromResponse);
};

const ProductImage = styled(Product.PrimaryImageWithLogos)({
  flex: "none",
});

const Designation = styled(Product.FullDesignation)({
  ...getFontStyle(FontSize.HeadlineMedium),
  fontWeight: "bold",
  ["i"]: {
    fontSize: "20px",
  },
});

const ProductCard = ({ product }: ProductCardProps) => {
  return (
    <ProductContext value={product}>
      <Card className={classes.productCard}>
        <ProductImage />
        <Stacker gap={8}>
          <Designation className={classes.designation} />

          <Typography variant={"bodyLarge"} component={Product.ShortDescription} />
        </Stacker>
      </Card>
    </ProductContext>
  );
};

const DataSheetDownload = ({ product }: DataSheetDownloadProps) => {
  const { val: snackbar, setTrue: openSnackbar, setFalse: closeSnackbar } = useBooleanState(false);
  const { t } = useTranslation();

  const unitMode = useUnitMode();

  const handleDownload = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (window as any)._bannerInteractedPromise;
      await downloadPdf(product, unitMode);
      openSnackbar();
    } catch (error) {
      console.error("Error downloading product datasheet", error);
    }
  };

  useOnMount(() => {
    handleDownload();
    trackIntermediatePageLanding(
      product.designation,
      product.code,
      !!window.SCHA.visitedSalesPartner,
      window.SCHA.visitedSalesPartner?.name ?? undefined,
      window.SCHA.visitedSalesPartner?.affiliateId ?? undefined,
    );
  });
  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <h1 className={classes.title}>{t("datasheet.page.title")}</h1>
        <h2 className={classes.providedBy}>
          <Trans i18nKey={"datasheet.page.provided"}>
            this data sheet is provided by <Link href={HOMEPAGE}>medias.</Link>
          </Trans>
        </h2>
      </div>

      <ProductCard product={product} />
      <div className="text-center">
        <Trans i18nKey="datasheet.page.download">
          if download does not start, please <LinkButton onClick={handleDownload}>click here</LinkButton>
        </Trans>
      </div>

      <Snackbar
        open={snackbar}
        message={t("datasheet.page.snackbar")}
        action={<SnackbarCloseButton onClick={closeSnackbar} />}
      />
    </div>
  );
};

export default DataSheetDownload;
